class PredictiveSearch extends SearchForm {
	constructor() {
		super();

		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector(
			"[data-predictive-search]",
		);
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.positionFloatingResultsBound = this.positionFloatingResults.bind(this);

		this.setupEventListeners();
	}

	connectedCallback() {
		window.addEventListener("resize", this.positionFloatingResultsBound);
		window.addEventListener("scroll", this.positionFloatingResultsBound, {
			passive: true,
		});
	}

	disconnectedCallback() {
		window.removeEventListener("resize", this.positionFloatingResultsBound);
		window.removeEventListener("scroll", this.positionFloatingResultsBound);
		this.abortController.abort();
	}

	setupEventListeners() {
		if (this.input?.form) {
			this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		}

		if (this.input) {
			this.input.addEventListener("focus", this.onFocus.bind(this));
		}

		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
		this.addEventListener("keydown", this.onKeydown.bind(this));
	}

	getQuery() {
		return this.input ? this.input.value.trim() : "";
	}

	onChange() {
		super.onChange();

		const newSearchTerm = this.getQuery();

		if (
			!this.searchTerm ||
			(newSearchTerm && !newSearchTerm.startsWith(this.searchTerm))
		) {
			this.querySelector(".ws-predictive-results__content")?.remove();
		}

		this.updateSearchForTerm(this.searchTerm, newSearchTerm);
		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			this.close(true);
			return;
		}

		this.getSearchResults(this.searchTerm);
	}

	onFormSubmit(event) {
		if (
			!this.getQuery().length ||
			this.querySelector('[aria-selected="true"] a')
		) {
			event.preventDefault();
		}
	}

	onFormReset(event) {
		super.onFormReset(event);

		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();

		if (!currentSearchTerm.length) return;

		if (this.searchTerm !== currentSearchTerm) {
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		setTimeout(() => {
			if (!this.contains(document.activeElement)) {
				this.close();
			}
		});
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true);

		switch (event.code) {
			case "ArrowUp":
				event.preventDefault();
				this.switchOption("up");
				break;
			case "ArrowDown":
				event.preventDefault();
				this.switchOption("down");
				break;
			case "Enter":
				this.selectOption();
				break;
			case "Escape":
				this.close();
				this.input?.focus();
				break;
		}
	}

	onKeydown(event) {
		if (event.code === "ArrowUp" || event.code === "ArrowDown") {
			event.preventDefault();
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]",
		);
		const currentButtonText = searchForTextElement?.innerText;

		if (!currentButtonText) return;

		const regex = new RegExp(this.escapeRegExp(previousTerm), "g");
		const matches = currentButtonText.match(regex);

		if (matches && matches.length > 1) return;

		searchForTextElement.innerText = currentButtonText.replace(
			previousTerm,
			newTerm,
		);
	}

	escapeRegExp(string) {
		return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	}

	switchOption(direction) {
		if (!this.getAttribute("open")) return;

		const moveUp = direction === "up";
		const selectedElement = this.querySelector('[aria-selected="true"]');
		const allVisibleElements = Array.from(
			this.querySelectorAll(
				'li[role="option"], a[role="option"], button[role="option"]',
			),
		).filter((element) => element.offsetParent !== null);

		if (!allVisibleElements.length || (moveUp && !selectedElement)) return;

		const selectedIndex = selectedElement
			? allVisibleElements.indexOf(selectedElement)
			: -1;
		let activeIndex = 0;

		if (!moveUp && selectedElement) {
			activeIndex =
				selectedIndex === allVisibleElements.length - 1 ? 0 : selectedIndex + 1;
		} else if (moveUp) {
			activeIndex =
				selectedIndex <= 0 ? allVisibleElements.length - 1 : selectedIndex - 1;
		}

		const activeElement = allVisibleElements[activeIndex];

		selectedElement?.setAttribute("aria-selected", "false");
		activeElement.setAttribute("aria-selected", "true");
		this.input.setAttribute("aria-activedescendant", activeElement.id || "");
	}

	selectOption() {
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, a[aria-selected="true"], button[aria-selected="true"]',
		);
		selectedOption?.click();
	}

	getSearchResults(searchTerm) {
		if (!searchTerm || !this.predictiveSearchResults) return;

		const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			return;
		}

		this.abortController.abort();
		this.abortController = new AbortController();

		const params = new URLSearchParams({
			q: searchTerm,
			"resources[type]": "product",
			"resources[limit]": "8",
			"resources[limit_scope]": "all",
			"resources[options][unavailable_products]": "last",
			"resources[options][fields]": [
				"title",
				"body",
				"product_type",
				"tag",
				"vendor",
				"variants.title",
				"variants.sku",
				"variants.barcode",
			].join(","),
			section_id: "predictive-search",
		});

		fetch(`${routes.predictive_search_url}?${params.toString()}`, {
			signal: this.abortController.signal,
		})
			.then((response) => {
				if (!response.ok) {
					this.close();
					throw new Error(
						`Predictive search failed with status ${response.status}`,
					);
				}

				return response.text();
			})
			.then((text) => {
				const documentFragment = new DOMParser().parseFromString(
					text,
					"text/html",
				);
				const section = documentFragment.querySelector(
					"#shopify-section-predictive-search",
				);

				if (!section) {
					throw new Error(
						"Predictive search response did not include the predictive-search section.",
					);
				}

				const resultsMarkup = section.innerHTML;
				this.cachedResults[queryKey] = resultsMarkup;
				this.renderSearchResults(resultsMarkup);
			})
			.catch((error) => {
				if (error?.name === "AbortError" || error?.code === 20) return;

				this.close();
				console.error(error);
			});
	}

	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text") || "";

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		if (!this.statusElement) return;

		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText || "";

		setTimeout(() => {
			this.statusElement?.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.makeResultIdsUnique();
		this.setAttribute("results", true);
		this.setLiveRegionResults();
		this.open();
	}

	makeResultIdsUnique() {
		const scope =
			this.input?.id || `predictive-${Math.random().toString(36).slice(2, 8)}`;
		const idMap = new Map();

		this.predictiveSearchResults.querySelectorAll("[id]").forEach((element) => {
			const oldId = element.id;
			const newId = `${scope}-${oldId}`;
			idMap.set(oldId, newId);
			element.id = newId;
		});

		this.predictiveSearchResults
			.querySelectorAll("[aria-labelledby], [aria-controls]")
			.forEach((element) => {
				["aria-labelledby", "aria-controls"].forEach((attribute) => {
					const value = element.getAttribute(attribute);
					if (!value) return;

					const updatedValue = value
						.split(/\s+/)
						.map((id) => idMap.get(id) || id)
						.join(" ");

					element.setAttribute(attribute, updatedValue);
				});
			});
	}

	setLiveRegionResults() {
		this.removeAttribute("loading");
		const countElement = this.querySelector(
			"[data-predictive-search-live-region-count-value]",
		);
		this.setLiveRegionText(countElement?.textContent?.trim() || "");
	}

	positionFloatingResults() {
		if (
			this.dataset.floatingResults !== "true" ||
			!this.isOpen ||
			!this.predictiveSearchResults
		)
			return;

		const anchor =
			this.querySelector("[data-predictive-search-anchor]") || this.input;
		if (!anchor) return;

		const rect = anchor.getBoundingClientRect();
		const viewportWidth = document.documentElement.clientWidth;
		const viewportHeight = window.innerHeight;
		const margin = viewportWidth < 750 ? 10 : 16;
		const gap = 8;
		const panelWidth = Math.min(1180, viewportWidth - margin * 2);
		// Centre the panel on the viewport rather than on the search field.
		const left = (viewportWidth - panelWidth) / 2;
		const top = Math.max(margin, rect.bottom + gap);
		const availableHeight = Math.max(220, viewportHeight - top - margin);
		// Only cap the height: the panel is a flex column, so it shrinks to fit its
		// results and the grid scrolls once the cap is reached.
		const panelMaxHeight = Math.min(640, availableHeight);

		Object.assign(this.predictiveSearchResults.style, {
			top: `${Math.round(top)}px`,
			left: `${Math.round(left)}px`,
			width: `${Math.round(panelWidth)}px`,
			height: "auto",
			maxHeight: `${Math.round(panelMaxHeight)}px`,
		});
	}

	open() {
		this.setAttribute("open", true);
		this.input?.setAttribute("aria-expanded", "true");
		this.isOpen = true;
		requestAnimationFrame(() => this.positionFloatingResults());
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm && this.input) {
			this.input.value = "";
			this.searchTerm = "";
			this.removeAttribute("results");
		}

		this.querySelector('[aria-selected="true"]')?.setAttribute(
			"aria-selected",
			"false",
		);
		this.input?.setAttribute("aria-activedescendant", "");
		this.input?.setAttribute("aria-expanded", "false");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.predictiveSearchResults?.removeAttribute("style");
	}
}

if (!customElements.get("predictive-search")) {
	customElements.define("predictive-search", PredictiveSearch);
}
